import { Plus, Trash2, Coins } from "lucide-react";
import { formatCurrency, fromBaseCurrency, toBaseCurrency, getCurrencySymbol } from "../../lib/utils";

export const MIXED_METHODS = [
  { id: "cash", label: "Numerário" },
  { id: "card", label: "Multicaixa (TPA)" },
  { id: "transfer", label: "Transferência" },
  { id: "mobile", label: "Pag. Móvel" },
];

// Parcelas iniciais: numerário em branco + TPA com o resto (valores em moeda de apresentação)
export const emptyMixedParts = (total, tpas = []) => [
  { method: "cash", amount: "", tpa_id: "" },
  { method: "card", amount: fromBaseCurrency(total).toFixed(2), tpa_id: tpas.length === 1 ? tpas[0].tpa_id : "" },
];

export const mixedRemaining = (parts, total) => Math.round((fromBaseCurrency(total) - parts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)) * 100) / 100;

export const validateMixedParts = (parts, total, tpas) => {
  const filled = parts.filter((p) => parseFloat(p.amount) > 0);
  if (filled.length < 2) return "Pagamento misto requer pelo menos duas parcelas com valor";
  if (Math.abs(mixedRemaining(parts, total)) > 0.009) return "A soma das parcelas tem de ser igual ao total";
  if (filled.some((p) => p.method === "card" && tpas.length && !p.tpa_id)) return "Indique o TPA de cada parcela Multicaixa";
  return null;
};

export const mixedPayload = (parts) => ({
  payments: parts.filter((p) => parseFloat(p.amount) > 0).map((p) => ({ method: p.method, amount: toBaseCurrency(p.amount), tpa_id: p.method === "card" && p.tpa_id ? p.tpa_id : null })),
});

export const MixedPaymentEditor = ({ parts, onChange, total, tpas = [], dark = false }) => {
  const remaining = mixedRemaining(parts, total);
  const sel = dark ? "bg-muted border-border" : "bg-background border-input";
  const update = (i, patch) => onChange(parts.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const fillRest = (i) => update(i, { amount: Math.max(0, (parseFloat(parts[i].amount) || 0) + remaining).toFixed(2) });
  return (
    <div className="space-y-2 rounded-lg border p-2.5" data-testid="mixed-payment-editor">
      <p className="text-xs text-muted-foreground flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> Pagamento misto — indique o valor de cada parcela ({getCurrencySymbol()})</p>
      {parts.map((p, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5" data-testid={`mixed-part-${i}`}>
          <select className={`h-9 rounded-md border px-2 text-xs ${sel}`} value={p.method} onChange={(e) => update(i, { method: e.target.value, tpa_id: "" })} data-testid={`mixed-part-method-${i}`}>
            {MIXED_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          {p.method === "card" && tpas.length > 0 && (
            <select className={`h-9 rounded-md border px-2 text-xs ${sel}`} value={p.tpa_id} onChange={(e) => update(i, { tpa_id: e.target.value })} data-testid={`mixed-part-tpa-${i}`}>
              <option value="">TPA…</option>
              {tpas.map((t) => <option key={t.tpa_id} value={t.tpa_id}>{t.name}</option>)}
            </select>
          )}
          <input type="number" min="0" step="0.01" className={`h-9 w-28 rounded-md border px-2 text-sm font-mono ${sel}`} value={p.amount} onChange={(e) => update(i, { amount: e.target.value })} placeholder="0.00" data-testid={`mixed-part-amount-${i}`} />
          {remaining !== 0 && <button type="button" onClick={() => fillRest(i)} className="h-9 px-2 rounded-md border text-[11px] hover:border-emerald-500/60" data-testid={`mixed-part-rest-${i}`}>resto</button>}
          {parts.length > 2 && <button type="button" onClick={() => onChange(parts.filter((_, j) => j !== i))} className="h-9 w-9 rounded-md border flex items-center justify-center text-red-500" data-testid={`mixed-part-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      ))}
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={() => onChange([...parts, { method: "transfer", amount: "", tpa_id: "" }])} className="flex items-center gap-1 text-emerald-600 hover:underline" data-testid="mixed-part-add"><Plus className="w-3.5 h-3.5" /> Parcela</button>
        <span className={`font-mono font-semibold ${Math.abs(remaining) > 0.009 ? "text-amber-500" : "text-emerald-500"}`} data-testid="mixed-remaining">
          {remaining > 0.009 ? `Falta ${formatCurrency(toBaseCurrency(remaining))}` : remaining < -0.009 ? `Excesso ${formatCurrency(toBaseCurrency(-remaining))}` : "Parcelas = total ✓"}
        </span>
      </div>
    </div>
  );
};
